import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { SetPage } from '../../interfaces';

interface Options {
    pageCount: number;
    page: number;
    setPage: SetPage;
}

interface Returned {
    buttonsData: number[];
    prevShown: boolean;
    nextShown: boolean;
    toStartShown: boolean;
    toEndShown: boolean;
    prev: () => void;
    next: () => void;
    toStart: () => void;
    toEnd: () => void;
    buttonClick: MouseEventHandler;
}

export function usePageSelect(options: Options): Returned {
    const { pageCount, page, setPage } = options;

    const visibleButtonsCount = 3;

    const [buttonsData, setButtonsData] = useState<number[]>([]);
    const [prevShown, setPrevShown] = useState<boolean>(true);
    const [nextShown, setNextShown] = useState<boolean>(true);
    const [toStartShown, setToStartShown] = useState<boolean>(true);
    const [toEndShown, setToEndShown] = useState<boolean>(true);

    useEffect(() => {
        if (page === pageCount) setNextShown(false);
        else setNextShown(true);
        if (page === 1) setPrevShown(false);
        else setPrevShown(true);
        if (page >= pageCount - 1) setToEndShown(false);
        else setToEndShown(true);
        if (page <= 2) setToStartShown(false);
        else setToStartShown(true);
    }, [page, pageCount]);

    useEffect(() => {
        const buttons: number[] = [];
        const step = Math.floor(visibleButtonsCount / 2);
        let start;
        let end;
        if (pageCount > visibleButtonsCount) {
            end = page + step >= pageCount ? pageCount : page + step;
            start = end - visibleButtonsCount < 1 ? 1 : end - visibleButtonsCount;
            if (end - start < visibleButtonsCount)
                end = start + visibleButtonsCount >= pageCount ? pageCount : start + visibleButtonsCount;
        } else {
            start = 1;
            end = pageCount;
        }
        for (let i = start; i <= end; i++) {
            buttons.push(i);
        }
        setButtonsData(buttons);
    }, [pageCount, page, visibleButtonsCount]);

    const prev = useCallback(() => {
        setPage(page - 1);
    }, [page, setPage]);

    const next = useCallback(() => {
        setPage(page + 1);
    }, [page, setPage]);

    const toStart = useCallback(() => {
        setPage(1);
    }, [setPage]);

    const toEnd = useCallback(() => {
        setPage(pageCount);
    }, [pageCount, setPage]);

    const buttonClick: MouseEventHandler = useCallback(
        (event) => {
            //@ts-ignore
            setPage(+event.target?.id?.split('-')[1]);
        },
        [setPage],
    );

    return {
        buttonsData,
        prevShown,
        nextShown,
        toStartShown,
        toEndShown,
        prev,
        next,
        toStart,
        toEnd,
        buttonClick,
    };
}
